import { useState } from "react";
import { motion } from "framer-motion";
import { Navigation, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/data/content";
import { Eyebrow, Reveal } from "./Reveal";

const inputClass =
  "w-full rounded-xl border border-[#1B3B2B]/15 bg-[#FFFDF9] px-4 py-3.5 text-sm text-[#1B3B2B] placeholder-[#6E8779]/70 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#C85A32] focus:ring-2 focus:ring-[#C85A32]/20";

const MapPlaceholder = () => (
  <div
    data-testid="map-placeholder"
    className="relative aspect-[4/3] overflow-hidden rounded-[28px] border border-[#1B3B2B]/12 bg-[#F4EFE6] shadow-[0_24px_60px_rgba(27,59,43,0.1)]"
  >
    <svg viewBox="0 0 400 300" className="absolute inset-0 h-full w-full" aria-hidden="true">
      <rect width="400" height="300" fill="#F4EFE6" />
      <path d="M0 78 C 90 70, 180 96, 400 60" stroke="#1B3B2B" strokeOpacity="0.14" strokeWidth="10" fill="none" />
      <path d="M60 0 C 70 110, 50 210, 90 300" stroke="#1B3B2B" strokeOpacity="0.12" strokeWidth="7" fill="none" />
      <path d="M200 0 C 190 100, 230 200, 210 300" stroke="#1B3B2B" strokeOpacity="0.12" strokeWidth="7" fill="none" />
      <path d="M0 180 C 120 190, 260 160, 400 185" stroke="#1B3B2B" strokeOpacity="0.12" strokeWidth="7" fill="none" />
      <path d="M320 0 C 330 90, 300 200, 340 300" stroke="#1B3B2B" strokeOpacity="0.1" strokeWidth="5" fill="none" />
      <path d="M0 250 C 140 240, 260 260, 400 245" stroke="#1B3B2B" strokeOpacity="0.1" strokeWidth="5" fill="none" />
      <rect x="230" y="110" width="110" height="70" rx="6" fill="#EAE2D3" />
      <text x="285" y="140" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2" fill="#1B3B2B" fillOpacity="0.55">
        PLACE
      </text>
      <text x="285" y="155" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2" fill="#1B3B2B" fillOpacity="0.55">
        DES LICES
      </text>
      <path d="M230 180 L340 110" stroke="#C85A32" strokeOpacity="0.35" strokeWidth="2" strokeDasharray="5 6" />
    </svg>

    <div className="absolute left-[52%] top-[38%] -translate-x-1/2 -translate-y-1/2">
      <span className="absolute -inset-4 animate-ping rounded-full bg-[#C85A32]/25" />
      <span className="relative flex h-11 w-11 items-center justify-center rounded-full border-4 border-[#FAF7F2] bg-[#C85A32] text-[#FAF7F2] shadow-lg">
        <svg viewBox="0 0 64 64" className="h-5 w-5" aria-hidden="true">
          <ellipse cx="32" cy="34" rx="10" ry="14" fill="#FAF7F2" />
          <path d="M32 22 q 7 12 0 24" stroke="#C85A32" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        </svg>
      </span>
    </div>

    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between border-t border-[#1B3B2B]/10 bg-[#FAF7F2]/85 px-5 py-3 backdrop-blur-sm">
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1B3B2B]/60">
        48.1119° N — 1.6810° O
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C85A32]">
        Carte indicative
      </span>
    </div>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const onSubmit = (e) => {
    e.preventDefault();
    toast.success("Merci ! Votre message a bien été envoyé.", {
      description: "Nous vous répondrons sous 48 heures. À très vite.",
    });
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#FAF7F2] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow>Nous trouver</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              data-testid="contact-title"
              className="mt-6 font-display text-3xl font-semibold tracking-tight text-[#1B3B2B] sm:text-4xl lg:text-5xl"
            >
              Au coin de la <em className="italic text-[#C85A32]">place</em>.
            </h2>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <MapPlaceholder />
            </Reveal>
            <Reveal delay={0.12}>
              <motion.a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="directions-button"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="mt-6 inline-flex items-center gap-3 rounded-full bg-[#1B3B2B] px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#F5F0E6] transition-colors duration-300 hover:bg-[#C85A32]"
              >
                <Navigation size={15} />
                Obtenir l'itinéraire
              </motion.a>
            </Reveal>
          </div>

          <div>
            <Reveal delay={0.1}>
              <h3 className="font-display text-2xl font-semibold text-[#1B3B2B]">
                Privatisations &amp; grandes tablées
              </h3>
              <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-[#3D5A4B]/90">
                Un anniversaire, une réunion d'équipe, un goûter de presse ?
                Écrivez-nous — la salle du fond se privatise le soir sur
                réservation.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <form data-testid="contact-form" onSubmit={onSubmit} className="mt-8 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input
                    data-testid="contact-form-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Votre nom"
                    className={inputClass}
                  />
                  <input
                    data-testid="contact-form-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Votre e-mail"
                    className={inputClass}
                  />
                </div>
                <textarea
                  data-testid="contact-form-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Dites-nous tout : date, nombre de convives, envies…"
                  className={`${inputClass} resize-none`}
                />
                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#C85A32] px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#FAF7F2] transition-colors duration-300 hover:bg-[#A84420]"
                >
                  Envoyer le message
                  <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
